// Avatar Studio. Owned items only; locked ones point to the shop.

import { h, clear, svg } from '@kit/dom';
import { ITEMS, SKIN_TONES, HAIR_COLORS, HAIR_STYLES, EYE_STYLES, MOUTH_STYLES, type Slot } from '@avatar/model';
import { avatarEl } from '@avatar/render';
import { canPaint } from '@avatar/painted';
import { onArtReady } from '@kit/assets';
import { getSave, update, owns } from '../state';
import { topBar } from '../ui';
import { go } from '../router';

type Tab = 'body' | 'hair' | 'face' | 'top' | 'bottom' | 'shoes' | 'hat' | 'accessory' | 'back' | 'name';

export function studioScreen(root: HTMLElement) {
  const first = !getSave().onboarded;
  let tab: Tab = first ? 'body' : 'top';
  const bar = topBar(first ? 'Make your explorer' : 'Studio', { back: first ? undefined : 'hub', pills: !first });
  const pic = h('div.center', { style: { padding: '4px 0' } });
  const tabs = h('div.tabs');
  const panel = h('div', { style: { padding: '0 16px 16px' } });
  const done = h('div.pad', null, h('button.btn.primary.block', {
    onclick: () => { update(s => { s.onboarded = true; }); go('hub'); },
  }, first ? 'Go to Base Camp' : 'Done'));
  root.append(bar, pic, tabs, panel, done);
  if (first) root.insertBefore(h('p.muted.center', { style: { padding: '0 16px' } }, 'This is you. Pick a look, then head outside.'), pic);

  const TABS: [Tab, string][] = [['body', 'Body'], ['hair', 'Hair'], ['face', 'Face'], ['top', 'Tops'], ['bottom', 'Bottoms'], ['shoes', 'Shoes'], ['hat', 'Hats'], ['accessory', 'Extras'], ['back', 'Back'], ['name', 'Name']];

  function renderPic() { clear(pic); pic.append(avatarEl(getSave().avatar, 170)); }

  function renderTabs() {
    clear(tabs);
    for (const [id, label] of TABS) tabs.append(h('button.tab' + (tab === id ? '.on' : ''), { onclick: () => { tab = id; renderTabs(); renderPanel(); } }, label));
  }

  function swatches(colors: string[], current: string, set: (c: string) => void): HTMLElement {
    return h('div.swatches', null, colors.map(c => h('button.swatch' + (c === current ? '.on' : ''), { style: { background: c }, 'aria-label': c, onclick: () => { set(c); renderAll(); } })));
  }

  function options<T extends string>(vals: readonly T[], current: T, set: (v: T) => void, preview: (v: T) => HTMLElement): HTMLElement {
    return h('div.grid3', null, vals.map(v => h('button.opt' + (v === current ? '.on' : ''), { onclick: () => { set(v); renderAll(); } }, preview(v), v)));
  }

  function slotOptions(slot: Slot, optional: boolean): HTMLElement {
    const a = getSave().avatar;
    const current = (a as any)[slot] as string | undefined;
    const items = ITEMS.filter(i => i.slot === slot);
    const kids: HTMLElement[] = [];
    if (optional) kids.push(h('button.opt' + (!current ? '.on' : ''), { onclick: () => { update(s => { delete (s.avatar as any)[slot]; }); renderAll(); } }, avatarEl({ ...a, [slot]: undefined } as any, 44), 'None'));
    for (const it of items) {
      const have = owns(it.id);
      kids.push(h('button.opt' + (current === it.id ? '.on' : '') + (have ? '' : '.locked'), {
        onclick: () => { if (have) { update(s => { (s.avatar as any)[slot] = it.id; }); renderAll(); } else go('shop'); },
      }, avatarEl({ ...a, [slot]: it.id } as any, 44), have ? it.name : `${it.price} gems`));
    }
    return h('div.grid3', null, kids);
  }

  function renderPanel() {
    clear(panel);
    const a = getSave().avatar;
    switch (tab) {
      case 'body':
        panel.append(
          h('h3', null, 'Skin'), swatches(SKIN_TONES, a.body.skin, c => update(s => { s.avatar.body.skin = c; })),
          // the painted figure has one body; shapes apply to the simple drawing only
          canPaint(a) ? '' : h('h3', { style: { marginTop: '12px' } }, 'Shape'),
          canPaint(a) ? '' : h('div.grid3', null, (['a', 'b', 'c'] as const).map(sh => h('button.opt' + (a.body.shape === sh ? '.on' : ''), { onclick: () => { update(s => { s.avatar.body.shape = sh; }); renderAll(); } }, avatarEl({ ...a, body: { ...a.body, shape: sh } }, 44), sh === 'a' ? 'Middle' : sh === 'b' ? 'Wide' : 'Slim'))),
        );
        break;
      case 'hair':
        panel.append(
          h('h3', null, 'Color'), swatches(HAIR_COLORS, a.hair.color, c => update(s => { s.avatar.hair.color = c; })),
          h('h3', { style: { marginTop: '12px' } }, 'Style'),
          options(HAIR_STYLES, a.hair.style as any, v => update(s => { s.avatar.hair.style = v; }), v => avatarEl({ ...a, hair: { ...a.hair, style: v } }, 56, 'face')),
        );
        break;
      case 'face':
        panel.append(
          h('h3', null, 'Eyes'), options(EYE_STYLES, a.eyes as any, v => update(s => { s.avatar.eyes = v; }), v => avatarEl({ ...a, eyes: v }, 56, 'face')),
          h('h3', { style: { marginTop: '12px' } }, 'Mouth'), options(MOUTH_STYLES, a.mouth as any, v => update(s => { s.avatar.mouth = v; }), v => avatarEl({ ...a, mouth: v }, 56, 'face')),
        );
        break;
      case 'top': panel.append(slotOptions('top', false)); break;
      case 'bottom': panel.append(slotOptions('bottom', false)); break;
      case 'shoes': panel.append(slotOptions('shoes', false)); break;
      case 'hat': panel.append(slotOptions('hat', true)); break;
      case 'accessory': panel.append(slotOptions('accessory', true)); break;
      case 'back': panel.append(slotOptions('back', true)); break;
      case 'name': {
        const input = h('input', { type: 'text', value: a.persona.name, maxLength: 16, placeholder: 'Your explorer name' }) as HTMLInputElement;
        input.addEventListener('input', () => update(s => { s.avatar.persona.name = input.value.slice(0, 16); }));
        panel.append(h('h3', null, 'Name'), input, h('p.muted.small', { style: { marginTop: '8px' } }, 'Titles like "Rock Hound" appear here as you earn them.'));
        if (a.persona.title) panel.append(h('p', null, `Title: ${a.persona.title}`));
        break;
      }
    }
  }

  function renderAll() { renderPic(); renderTabs(); renderPanel(); }
  renderAll();
  // art arrives image by image after the screen opens; redraw once it settles
  let t = 0;
  const unArt = onArtReady(() => { clearTimeout(t); t = window.setTimeout(renderAll, 200); });
  return () => { unArt(); clearTimeout(t); };
}
